import { useEffect, useMemo, useState, useRef, useContext } from 'react'
import { View, Text, TouchableOpacity, StyleSheet, Dimensions, Image, Animated, Modal, Pressable, ScrollView } from 'react-native'
import ViewShot from 'react-native-view-shot'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { pickCacheGet, pickCacheSet } from '../../components/Pick/cache'
import { EmptyState, formatRiderNameParts, NationalFlag, RiderName, stripTeamBracket, RiderIdent } from '../../components/Pick/ui'
import { colors, fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useAvatars } from '../../lib/useAvatars'
import PersonAvatarFace from '../../components/PersonAvatarFace'
import { PickScrollContext } from '../../components/Pick/scrollContext'
import { LinearGradient } from 'expo-linear-gradient'
import { darken, lighten } from '../../components/personAvatarConfig'
import { raceLogo } from '../../lib/raceLogos'
import OdrScout from '../../components/Pick/OdrScout'
import PersonAvatarBust from '../../components/PersonAvatarBust'
import { TeamJersey } from '../../components/RiderFace'
import RiderFace from '../../components/RiderFace'
import { fetchOdrResults, buildResults, makeEffective, fetchStageResults, fmtGap } from '../../components/Pick/odrResults'
import { loadRiderInfo } from '../../components/Pick/riderInfo'
import { useAuth } from '../../lib/AuthContext'
import { useRouter } from 'expo-router'
import { LOGO } from '../../lib/assets'
import { legibleOn } from '../../lib/contrast'

export default function TabellaLive({ viewShotRef, idStage, idRace, prevStageId, isTour, nickname, leagueId, colors: raceColors, onDataReady, shareHeader, shareMode = 'top25', finished = false }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { t } = useTranslation()
  const [rows, setRows] = useState({ pivot: [], slots: [] })
  const [loading, setLoading] = useState(true)
  const [expandedUsers, setExpandedUsers] = useState({})
  const [jerseys, setJerseys] = useState({})
  const [flips, setFlips] = useState({})
  // Gara di un giorno: id dei corridori scelti, maglia/nazionalita' dalla start list (per il dettaglio del giocatore)
  const [oinfo, setOinfo] = useState({ riders: {}, userRiders: {}, national: false })

  useEffect(() => {
    if (!idStage || !leagueId || !idRace) return
    let cancelled = false
    ;(async () => {
      const [pRes, fRes, cRes] = await Promise.all([
        supabase.from('fact_user_pick').select('id_user, id_slot, id_rider').eq('id_stage', idStage).eq('id_league', leagueId),
        supabase.from('fact_user_flip').select('id_rider').eq('id_race', idRace).eq('id_league', leagueId).eq('id_stage_used', idStage),
        supabase.from('dim_race').select('id_race_category').eq('id_race', idRace).maybeSingle(),
      ])
      if (cancelled) return
      const userRiders = {}
      ;(pRes.data ?? []).forEach(r => { (userRiders[r.id_user] ??= {})[r.id_slot] = r.id_rider })

      // Anagrafica dei soli corridori scelti (e dei flip): vedi components/Pick/riderInfo.js
      const ids = [...new Set([...(pRes.data ?? []).map(r => r.id_rider), ...(fRes.data ?? []).map(r => r.id_rider)])].filter(Boolean)
      const riders = await loadRiderInfo(ids, idRace)
      if (!cancelled) setOinfo({ riders, userRiders, national: cRes.data?.id_race_category === 5 })
    })().catch(() => {})
    return () => { cancelled = true }
  }, [idStage, idRace, leagueId])

  useEffect(() => {
    if (!idStage || !leagueId || !idRace) return
    let cancelled = false
    const cacheKey = `picks_live_${leagueId}_${idStage}`

    const loadCache = async () => {
      const cached = await pickCacheGet(cacheKey)
      if (cached && !cancelled) {
        setRows(cached.rows); setExpandedUsers(cached.expanded); setLoading(false)
        onDataReady?.({ rows: cached.rows, jerseys: {}, colors: raceColors })
      } else { setLoading(true) }
    }
    loadCache()

    const processData = async (data) => {
      if (!data || data.length === 0) { setRows({ pivot: [], slots: [] }); setLoading(false); return }
      const map = {}; const slots = new Set(); const initialExpanded = {}
      data.forEach(r => {
        if (!map[r.display_name]) { map[r.display_name] = {}; initialExpanded[r.display_name] = true; map[r.display_name]._autopick = false; map[r.display_name]._id_user = r.id_user; map[r.display_name]._surnames = {} }
        map[r.display_name][r.id_slot] = r.rider_name_short
        map[r.display_name]._surnames[r.id_slot] = { surname: r.rider_surname, name: r.rider_name }
        if (r.is_autopick) map[r.display_name]._autopick = true
        slots.add(r.id_slot)
      })
      const sortedSlots = [...slots].sort((a, b) => a - b)
      const pivot = Object.entries(map).map(([name, picks]) => {
        const surnames = picks._surnames ?? {}; const isAutopick = picks._autopick ?? false; const id_user = picks._id_user
        const cleanPicks = { ...picks }; delete cleanPicks._surnames; delete cleanPicks._autopick; delete cleanPicks._id_user
        return { name, picks: cleanPicks, surnames, isAutopick, id_user }
      }).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
      const newRows = { pivot, slots: sortedSlots }
      if (!cancelled) {
        setRows(newRows); setExpandedUsers(initialExpanded); setLoading(false)
        onDataReady?.({ rows: newRows, jerseys: {}, colors: raceColors })
        await pickCacheSet(cacheKey, { rows: newRows, expanded: initialExpanded })
      }
    }

    const queries = [
      supabase.rpc('fn_user_pick_race', { p_id_stage: idStage, p_id_league: leagueId }).select('display_name, rider_name_short, rider_surname, rider_name, id_slot, is_autopick, id_user'),
      supabase.from('fact_user_flip').select('id_user, id_slot, id_rider').eq('id_race', idRace).eq('id_league', leagueId).eq('id_stage_used', idStage),
    ]
    if (isTour && prevStageId) {
      // titolari di rosa / ciclamino / azzurra dopo la tappa precedente: funzione unica con gli spareggi (sql/20261028110000_maglie_tour_titolari.sql)
      queries.push(supabase.rpc('fn_tour_jerseys', { p_id_league: leagueId, p_id_race: idRace, p_id_stage: prevStageId }))
    }

    Promise.all(queries).then(async ([picksRes, flipRes, jerseyRes]) => {
      if (cancelled) return
      await processData(picksRes.data)
      const flipRows = flipRes.data ?? []
      if (flipRows.length > 0) {
        const riderIds = [...new Set(flipRows.map(f => f.id_rider))]
        const { data: riderData } = await supabase.from('dim_rider').select('id_rider, surname, name').in('id_rider', riderIds)
        if (!cancelled) {
          const riderMap = Object.fromEntries((riderData ?? []).map(r => [r.id_rider, r]))
          const flipMap = {}
          flipRows.forEach(f => { const rd = riderMap[f.id_rider]; if (rd) flipMap[f.id_user] = { id_slot: f.id_slot, id_rider: f.id_rider, rider_name_short: `${rd.surname} ${rd.name.charAt(0)}.` } })
          setFlips(flipMap)
        }
      } else if (!cancelled) setFlips({})

      if (!isTour || !prevStageId || !jerseyRes || jerseyRes.error) { setJerseys({}); return }
      const jerseyMap = {}
      for (const r of jerseyRes.data ?? []) jerseyMap[r.id_user] = r.jersey === 'gm' ? 'gpm' : r.jersey
      if (!cancelled) { setJerseys(jerseyMap); onDataReady?.({ rows: undefined, jerseys: jerseyMap, colors: raceColors }) }
    })
    return () => { cancelled = true }
  }, [idStage, prevStageId, idRace, isTour, leagueId])


  if (loading) return <View style={{ padding: 16 }}><Text style={{ color: tc.textMuted, fontSize: 13 }}>{t('pick.loading_lineups')}</Text></View>
  if (!rows.pivot.length) return <EmptyState icon="🚴" title={t('pick.no_picks_tabella')} subtitle={t('pick.no_picks_tabella_sub')} />

  // ── Giri a tappe: un corridore a tappa (tre nelle cronometro) → elenco per corridore con chi lo ha scelto ──
  if (isTour) {
    return <TourView rows={rows} flips={flips} nickname={nickname} tc={tc} info={oinfo} jerseys={jerseys} raceColors={raceColors} viewShotRef={viewShotRef} shareHeader={shareHeader} shareMode={shareMode} idRace={idRace} idStage={idStage} leagueId={leagueId} finished={finished} />
  }

  // ── Gara di un giorno: matrice giocatori × 5 corridori + immagine da condividere ──
  return <OdrView rows={rows} flips={flips} nickname={nickname} tc={tc} info={oinfo} viewShotRef={viewShotRef} shareHeader={shareHeader} shareMode={shareMode} idRace={idRace} idStage={idStage} leagueId={leagueId} finished={finished} />
}

// ── Gara di un giorno ───────────────────────────────────────────────────────
const NAME_W = 76
const OVERLAY_H = 120   // altezza massima dell'intestazione fissa (colonne + la tua riga)
const riderKey = (s) => String(s ?? '').trim().toLowerCase()

// Statistiche condivise da matrice e immagine: chiave del corridore (id vero o nome breve), quanti lo hanno scelto, il piu scelto
function useOdrStats(rows, info) {
  const keyOf = (id_user, slot, short) => {
    const id = info.userRiders[id_user]?.[slot]
    return id ? `id:${id}` : riderKey(short)
  }
  const stats = useMemo(() => {
    const counts = {}
    rows.pivot.forEach(r => { new Set(rows.slots.map(sl => keyOf(r.id_user, sl, r.picks[sl])).filter(Boolean)).forEach(k => { counts[k] = (counts[k] ?? 0) + 1 }) })
    let top = null, uniqueCount = 0
    Object.entries(counts).forEach(([k, c]) => { if (c === 1) uniqueCount++; if (!top || c > top.count) top = { key: k, count: c } })
    // chi ha scelto ciascun corridore e come si chiama (breve per le schede, intero per i dettagli)
    const owners = {}, nameOf = {}
    rows.pivot.forEach(r => rows.slots.forEach(sl => {
      const k = keyOf(r.id_user, sl, r.picks[sl])
      if (!k) return
      const list = (owners[k] ??= [])
      if (!list.some(o => o.id_user === r.id_user)) list.push({ id_user: r.id_user, name: r.name })
      if (!nameOf[k]) {
        const p = formatRiderNameParts(r.picks[sl])
        const id = info.userRiders[r.id_user]?.[sl]
        const rd = id ? info.riders[id] : null
        nameOf[k] = { short: p.bottom || p.top, full: rd ? (rd.labelNew || rd.label) : r.picks[sl] }
      }
    }))
    if (top) top.name = nameOf[top.key]?.short
    return { counts, top, uniqueCount, owners, nameOf }
  }, [rows, info])
  return { keyOf, ...stats, total: rows.pivot.length }
}

function OdrView({ rows, flips, nickname, tc, info, viewShotRef, shareHeader, shareMode, idRace, idStage, leagueId, finished }) {
  const stats = useOdrStats(rows, info)
  const [picked, setPicked] = useState(null)   // corridore evidenziato nella matrice (anche da Scout)
  const meRow = rows.pivot.find(r => r.name.toLowerCase() === nickname?.toLowerCase()) ?? null

  // Com'e' andata: a gara conclusa si caricano risultati e tabella punti (solo dei corridori scelti + il vincitore)
  const [raw, setRaw] = useState(null)
  useEffect(() => {
    if (!finished || !idStage) return
    const ids = []
    Object.values(info.userRiders).forEach(m => Object.values(m).forEach(id => ids.push(id)))
    Object.values(flips).forEach(f => f.id_rider && ids.push(f.id_rider))
    if (!ids.length) return
    let cancelled = false
    fetchOdrResults(idStage, ids).then(r => { if (!cancelled) setRaw(r) }).catch(() => {})
    return () => { cancelled = true }
  }, [finished, idStage, info, flips])
  const result = useMemo(() => (raw?.hasResults ? buildResults({ rows, info, flips, results: raw, meRow }) : null), [raw, rows, info, flips, meRow])

  return (
    <>
      {finished && <FinishedBanner idRace={idRace} idStage={idStage} leagueId={leagueId} tc={tc} />}
      <OdrScout rows={rows} stats={stats} meRow={meRow} onPickRider={setPicked} result={result} />
      <OdrMatrix rows={rows} flips={flips} nickname={nickname} tc={tc} info={info} stats={stats} picked={picked} setPicked={setPicked} />
      <ViewShot
        ref={viewShotRef}
        options={{ format: 'png', quality: 1, pixelRatio: 3 }}
        style={{ position: 'absolute', top: -9999, left: 0, width: SHARE_W }}
      >
        <OdrShareCard tc={tc} rows={rows} info={info} stats={stats} flips={flips} nickname={nickname} shareHeader={shareHeader} shareMode={shareMode} />
      </ViewShot>
    </>
  )
}

// A gara conclusa: com'e' andata a te, con il collegamento alla Classifica (la matrice resta una vista delle scelte)
function FinishedBanner({ idRace, idStage, leagueId, tc }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const router = useRouter()
  const [mine, setMine] = useState(null)

  useEffect(() => {
    if (!user?.id || !idStage || !leagueId) return
    let cancelled = false
    supabase.rpc('fn_simulazione_punti', { p_id_league: leagueId })
      .select('posizione_classifica, id_user, punti_totali').eq('id_stage', idStage).eq('id_user', user.id).maybeSingle()
      .then(({ data }) => { if (!cancelled) setMine(data ?? null) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [user?.id, idStage, leagueId])

  return (
    <TouchableOpacity
      onPress={() => router.push(`/leaderboard?id_race=${idRace}&id_stage=${idStage}`)}
      activeOpacity={0.8}
      accessibilityRole="button"
      accessibilityLabel={t('pick.finished_cta')}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginHorizontal: 12, marginTop: 8, padding: 12, minHeight: 48, borderRadius: 12, borderWidth: 1, backgroundColor: tc.rosaSubtle, borderColor: tc.rosaBorder }}
    >
      <MaterialCommunityIcons name="flag-checkered" size={20} color={tc.rosaText} />
      <Text style={{ flex: 1, fontSize: 13, color: tc.text }} numberOfLines={2}>
        {mine ? t('pick.finished_banner', { pos: mine.posizione_classifica, pts: mine.punti_totali }) : t('pick.finished_banner_nopos')}
      </Text>
      <Text style={{ fontSize: 12, fontFamily: fonts.condensedBold, color: tc.rosaText }}>{t('pick.finished_cta')}</Text>
      <MaterialCommunityIcons name="chevron-right" size={18} color={tc.rosaText} />
    </TouchableOpacity>
  )
}

function OdrMatrix({ rows, flips, nickname, tc, info, stats, picked, setPicked, jerseyColors, jerseyInfo }) {
  const { t } = useTranslation()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const scrollCtx = useContext(PickScrollContext)
  const containerRef = useRef(null)
  const [sheetUser, setSheetUser] = useState(null) // giocatore di cui si apre il dettaglio
  const [stuck, setStuck] = useState(false)        // la pagina e' scesa oltre l'intestazione: la mostro fissa in alto
  const single = rows.slots.length === 1          // un solo corridore a testa (tappe in linea): riga dedicata, senza colonne
  const avatars = useAvatars(single ? rows.pivot.map(r => r.id_user) : [])

  const { keyOf, counts, total } = stats
  const meRow = rows.pivot.find(r => r.name.toLowerCase() === nickname?.toLowerCase()) ?? null
  const ordered = meRow ? [meRow, ...rows.pivot.filter(r => r !== meRow)] : rows.pivot
  const myKeys = useMemo(() => new Set(meRow ? rows.slots.map(sl => keyOf(meRow.id_user, sl, meRow.picks[sl])).filter(Boolean) : []), [meRow, rows.slots, info])
  const pickedName = picked ? (() => {
    for (const r of rows.pivot) for (const sl of rows.slots) if (keyOf(r.id_user, sl, r.picks[sl]) === picked) { const p = formatRiderNameParts(r.picks[sl]); return p.bottom || p.top }
    return ''
  })() : ''

  // intestazione fissa: a ogni scroll controllo dove sta la matrice rispetto al bordo alto dell'area che scorre
  useEffect(() => {
    const check = () => {
      containerRef.current?.measureInWindow?.((x, y, w, h) => {
        const top = y - scrollCtx.getViewportTop()
        const next = top < -30 && top + h > OVERLAY_H + 20
        setStuck(prev => (prev === next ? prev : next))
      })
    }
    check()
    return scrollCtx.subscribe(check)
  }, [scrollCtx, rows])

  // righe e intestazione (usate sia nella matrice sia nell'intestazione fissa)
  const renderHead = () => single ? null : (
    <View style={[styles.mxHead, { borderBottomColor: tc.border }]}>
      <View style={{ width: NAME_W }} />
      {rows.slots.map(sl => (
        <Text key={sl} style={[styles.mxHeadCell, { color: tc.textMuted }]}>{sl}</Text>
      ))}
    </View>
  )

  // un solo corridore: a sinistra faccia e nome del giocatore, a destra maglia, bandiera e nome del corridore
  const renderSingleRow = (row, idx, isMe) => {
    const { name, picks, isAutopick, id_user } = row
    const sl = rows.slots[0]
    const flip = flips[id_user] ?? null
    const id = info.userRiders[id_user]?.[sl]
    const rd = id ? info.riders[id] : null
    const key = keyOf(id_user, sl, picks[sl])
    const parts = formatRiderNameParts(picks[sl])
    const full = rd ? (info.national ? stripTeamBracket(rd.labelNew || rd.label) : (rd.labelNew || rd.label)) : `${parts.bottom || parts.top} ${parts.bottom ? parts.top : ''}`.trim()
    const count = key ? counts[key] ?? 0 : 0
    const isPicked = picked && key === picked
    const dim = picked && !isPicked
    const ji = jerseyInfo?.[id_user] ?? null
    const rowBg = isMe ? tc.rosaSubtle : idx % 2 === 0 ? tc.bgCard : tc.bgCard2
    return (
      <View key={name}>
        <View style={[styles.mxRow, { backgroundColor: rowBg, borderLeftColor: isMe ? tc.rosa : (jerseyColors?.[id_user] ? legibleOn(jerseyColors[id_user], rowBg, 1.6) : 'transparent'), minHeight: ji ? 60 : 56, alignItems: 'center' }, dim && { opacity: 0.35 }, isPicked && { backgroundColor: tc.rosa + '33' }]}>
          <TouchableOpacity style={styles.sgPlayer} onPress={() => setSheetUser(row)} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel={t('pick.matrix_open_player', { name })}>
            {ji
              ? <PersonAvatarBust userId={id_user} settings={avatars[id_user]} jersey={ji} width={38} height={42} />
              : <PersonAvatarFace userId={id_user} settings={avatars[id_user]} size={30} />}
            <View style={{ flexShrink: 1 }}>
              <Text style={[styles.sgPlayerName, { fontSize: 16, color: isMe ? tc.rosaText : tc.text }]} numberOfLines={1}>{name}</Text>
              {ji && <Text style={[styles.sgJersey, { color: legibleOn(ji.color, rowBg, 3) }]} numberOfLines={1}>{ji.label}</Text>}
            </View>
            {isAutopick && <View style={styles.autoBadge}><Text style={styles.autoBadgeText}>A</Text></View>}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.sgRider}
            onPress={() => key && setPicked(p => (p === key ? null : key))}
            disabled={!key}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={key ? `${name}, ${full}, ${t('pick.matrix_chosen_by_a11y', { count, total })}` : undefined}
          >
            {rd
              ? <RiderIdent rider={rd} isNationalRace={info.national} fullName teamMode="short" nameStyle={[styles.sgRiderName, { color: isMe ? tc.rosaText : tc.text }]} />
              : <View style={{ flex: 1, minWidth: 0 }}><RiderName name={full} style={[styles.sgRiderName, { color: isMe ? tc.rosaText : tc.text }]} /></View>}
            {count > 1 && <View style={[styles.sgCountPill, { backgroundColor: tc.rosaSubtle }]}><Text style={[styles.sgCountText, { color: tc.rosaText }]}>{count}/{total}</Text></View>}
            {flip && <MaterialCommunityIcons name="swap-horizontal" size={14} color={colors.flip} />}
          </TouchableOpacity>
        </View>
        {flip && (
          <View style={styles.mxFlipRow}>
            <MaterialCommunityIcons name="swap-horizontal" size={12} color={colors.flip} />
            <Text style={styles.mxFlipText}>{flip.rider_name_short}</Text>
            <Text style={[styles.mxFlipSlot, { color: tc.textMuted }]}>{t('pick.flip_slot_arrow', { slot: flip.id_slot })}</Text>
          </View>
        )}
      </View>
    )
  }

  const renderRow = (row, idx, isMe) => {
    if (single) return renderSingleRow(row, idx, isMe)
    const { name, picks, isAutopick, id_user } = row
    const flip = flips[id_user] ?? null
    const rowBg = isMe ? tc.rosaSubtle : idx % 2 === 0 ? tc.bgCard : tc.bgCard2
    const singleWord = !/\s/.test(name.trim())
    return (
      <View key={name}>
        <View style={[styles.mxRow, { backgroundColor: rowBg, borderLeftColor: isMe ? tc.rosa : (jerseyColors?.[id_user] ? legibleOn(jerseyColors[id_user], rowBg, 1.6) : 'transparent') }]}>
          <TouchableOpacity
            style={[styles.mxName, { width: NAME_W }]}
            onPress={() => setSheetUser(row)}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={t('pick.matrix_open_player', { name })}
          >
            <Text
              style={[styles.mxNameText, { color: isMe ? tc.rosaText : tc.text }]}
              numberOfLines={singleWord ? 1 : 2}
              adjustsFontSizeToFit={singleWord}
              minimumFontScale={0.75}
            >
              {name}
            </Text>
            {isAutopick && <View style={styles.autoBadge}><Text style={styles.autoBadgeText}>A</Text></View>}
          </TouchableOpacity>
          {rows.slots.map(sl => {
            const rider = picks[sl]
            const key = keyOf(id_user, sl, rider)
            const parts = formatRiderNameParts(rider)
            const surname = parts.bottom || parts.top
            const initial = parts.bottom ? parts.top : ''
            const unique = key && counts[key] === 1
            const isPicked = picked && key === picked
            const dim = picked && !isPicked
            const isFlipSlot = flip?.id_slot === sl
            return (
              <TouchableOpacity
                key={sl}
                style={[styles.mxCell, { borderLeftColor: tc.border }, isPicked && { backgroundColor: tc.rosa + '40' }, dim && { opacity: 0.3 }]}
                onPress={() => key && setPicked(p => (p === key ? null : key))}
                disabled={!key}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={key ? `${name}, ${surname} ${initial}, ${t('pick.matrix_chosen_by_a11y', { count: counts[key] ?? 0, total })}` : undefined}
              >
                <Text style={[styles.mxSurname, { color: isMe ? tc.rosaText : tc.text }]} numberOfLines={2} adjustsFontSizeToFit minimumFontScale={0.8}>{surname || '—'}</Text>
                {!!initial && <Text style={[styles.mxInitial, { color: tc.textMuted }]} numberOfLines={1}>{initial}</Text>}
                {unique && <View style={[styles.mxUnique, { backgroundColor: tc.rosa }]} />}
                {isFlipSlot && <MaterialCommunityIcons name="swap-horizontal" size={11} color={colors.flip} style={styles.mxFlipIcon} />}
              </TouchableOpacity>
            )
          })}
        </View>
        {flip && (
          <View style={styles.mxFlipRow}>
            <MaterialCommunityIcons name="swap-horizontal" size={12} color={colors.flip} />
            <Text style={styles.mxFlipText}>{flip.rider_name_short}</Text>
            <Text style={[styles.mxFlipSlot, { color: tc.textMuted }]}>{t('pick.flip_slot_arrow', { slot: flip.id_slot })}</Text>
          </View>
        )}
      </View>
    )
  }

  // pubblica l'intestazione fissa (colonne + la tua riga) sopra lo scroll quando serve
  useEffect(() => {
    if (!stuck) { scrollCtx.setSticky(null); return }
    scrollCtx.setSticky(
      <View style={{ backgroundColor: tc.bg, borderBottomWidth: 1, borderBottomColor: tc.border }}>
        {renderHead()}
        {meRow && renderRow(meRow, 0, true)}
      </View>
    )
    return () => scrollCtx.setSticky(null)
  // NB: tc e' un oggetto nuovo a ogni render (useThemeColors): nelle dipendenze solo i colori primitivi, altrimenti setSticky -> render -> effect -> ... in ciclo
  }, [stuck, picked, rows, flips, info, tc.bg, tc.border, tc.rosa, tc.rosaSubtle, tc.text, tc.textMuted, tc.bgCard, tc.bgCard2])

  return (
    <View ref={containerRef} collapsable={false} style={{ paddingBottom: 8 }}>
      {/* guida o corridore selezionato */}
      <View style={styles.mxInfo}>
        {picked ? (
          <>
            <Text style={[styles.mxInfoText, { color: tc.text }]} numberOfLines={1}>
              {t('pick.matrix_chosen_by', { name: pickedName, count: counts[picked] ?? 0, total })}
            </Text>
            <TouchableOpacity onPress={() => setPicked(null)} style={styles.mxClear} accessibilityRole="button" accessibilityLabel={t('pick.matrix_clear')}>
              <MaterialCommunityIcons name="close-circle" size={20} color={tc.textMuted} />
            </TouchableOpacity>
          </>
        ) : (
          <Text style={[styles.mxInfoText, { color: tc.textMuted }]}>{t('pick.matrix_hint')}</Text>
        )}
      </View>

      {renderHead()}
      {ordered.map((row, idx) => renderRow(row, idx, !!meRow && row.name === meRow.name))}

      {sheetUser && (
        <PlayerPicksSheet
          row={sheetUser}
          rows={rows}
          info={info}
          keyOf={keyOf}
          counts={counts}
          myKeys={myKeys}
          isMe={!!meRow && sheetUser.name === meRow.name}
          total={total}
          flip={flips[sheetUser.id_user] ?? null}
          jersey={jerseyInfo?.[sheetUser.id_user] ?? null}
          onClose={() => setSheetUser(null)}
        />
      )}
    </View>
  )
}

// ── Giri a tappe ────────────────────────────────────────────────────────────
// Un corridore per tappa (tre nelle cronometro): il colpo d'occhio e' "chi ha scelto chi", quindi la vista predefinita
// e' per corridore (con chi lo ha scelto); per giocatore resta la matrice compatta.
const JERSEY_FALLBACK = { gc: '#FFD700', pt: '#4CAF50', gpm: '#E91E63' }

function TourView({ rows, flips, nickname, tc, info, jerseys, raceColors, viewShotRef, shareHeader, shareMode, idRace, idStage, leagueId, finished }) {
  const { t } = useTranslation()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const stats = useOdrStats(rows, info)
  const [view, setView] = useState('player')   // 'player' (predefinita) | 'rider'
  const [picked, setPicked] = useState(null)
  const meRow = rows.pivot.find(r => r.name.toLowerCase() === nickname?.toLowerCase()) ?? null

  // colore della maglia di classifica (GC, punti, GPM) per giocatore
  // chi veste una maglia di classifica: colore, stile (pois per la GPM di Tour e Vuelta) ed etichetta
  const jerseyInfo = useMemo(() => {
    const out = {}
    Object.entries(jerseys).forEach(([uid, j]) => {
      const color = (j === 'gc' ? raceColors?.gc : j === 'pt' ? raceColors?.pt : raceColors?.gm) || JERSEY_FALLBACK[j]
      out[uid] = {
        kind: j, color,
        pattern: j === 'gpm' && raceColors?.gmPattern === 'dots' ? 'dots' : null,
        label: j === 'gc' ? t('home.jersey_gc') : j === 'pt' ? t('home.jersey_pt') : t('home.jersey_gm'),
      }
    })
    return out
  }, [jerseys, raceColors, t])
  const jerseyColors = useMemo(() => Object.fromEntries(Object.entries(jerseyInfo).map(([u, j]) => [u, j.color])), [jerseyInfo])

  return (
    <>
      {finished && <TourFinished rows={rows} info={info} flips={flips} meRow={meRow} idRace={idRace} idStage={idStage} leagueId={leagueId} tc={tc} />}
      <OdrScout rows={rows} stats={stats} meRow={meRow} onPickRider={setPicked} tour={{ jerseyInfo }} />

      <View style={styles.tvToggle}>
        {[['player', t('pick.view_by_player')], ['rider', t('pick.view_by_rider')]].map(([k, label]) => (
          <TouchableOpacity key={k} onPress={() => setView(k)} style={[styles.tvToggleBtn, view === k && { backgroundColor: tc.rosa }]} accessibilityRole="tab" accessibilityState={{ selected: view === k }}>
            <Text style={[styles.tvToggleText, { color: view === k ? tc.brandText : tc.textMuted }]}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {view === 'rider'
        ? <RiderGroups rows={rows} stats={stats} info={info} flips={flips} meRow={meRow} jerseyColors={jerseyColors} tc={tc} />
        : <OdrMatrix rows={rows} flips={flips} nickname={nickname} tc={tc} info={info} stats={stats} picked={picked} setPicked={setPicked} jerseyColors={jerseyColors} jerseyInfo={jerseyInfo} />}

      <ViewShot
        ref={viewShotRef}
        options={{ format: 'png', quality: 1, pixelRatio: 3 }}
        style={{ position: 'absolute', top: -9999, left: 0, width: SHARE_W }}
      >
        <TourShareCard tc={tc} rows={rows} info={info} stats={stats} nickname={nickname} shareHeader={shareHeader} shareMode={shareMode} view={view} jerseyInfo={jerseyInfo} />
      </ViewShot>
    </>
  )
}

// elenco per corridore: nome, quanti lo hanno scelto e chi
function groupsOf(stats, info) {
  return Object.keys(stats.counts).map(k => {
    const id = k.startsWith('id:') ? Number(k.slice(3)) : null
    return { key: k, id, rider: id ? info.riders[id] : null, count: stats.counts[k], name: stats.nameOf[k]?.full ?? '', short: stats.nameOf[k]?.short ?? '', owners: stats.owners[k] ?? [] }
  }).sort((a, b) => b.count - a.count || a.short.localeCompare(b.short))
}

function RiderGroups({ rows, stats, info, flips, meRow, jerseyColors, tc }) {
  const { t } = useTranslation()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const avatars = useAvatars(rows.pivot.map(r => r.id_user))
  const [expanded, setExpanded] = useState({})
  const groups = useMemo(() => groupsOf(stats, info), [stats, info])
  const total = stats.total
  const flipList = rows.pivot.filter(r => flips[r.id_user])

  return (
    <View style={{ paddingBottom: 8 }}>
      <Text style={[styles.mxInfoText, { color: tc.textMuted, paddingHorizontal: 12, paddingVertical: 8 }]}>
        {t('pick.tour_summary', { riders: groups.length, players: total })}
      </Text>
      {groups.map((g, idx) => {
        const mine = !!meRow && g.owners.some(o => o.id_user === meRow.id_user)
        const open = expanded[g.key]
        const owners = open ? g.owners : g.owners.slice(0, 8)
        return (
          <View key={g.key} style={[styles.rgCard, { backgroundColor: idx % 2 === 0 ? tc.bgCard : tc.bgCard2, borderColor: mine ? tc.rosa : tc.border }]}>
            <View style={styles.rgHead}>
              {g.rider ? <RiderFace avatar={g.rider.avatar} kit={info.national ? null : g.rider.kit} size={46} shape="bust" /> : null}
              <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
                {g.rider
                  ? <RiderIdent rider={g.rider} isNationalRace={info.national} fullName teamMode="short" nameStyle={[styles.rgName, { color: tc.text }]} />
                  : <RiderName name={g.name || g.short} style={[styles.rgName, { color: tc.text }]} />}
                {mine && <Text style={[styles.rgMine, { color: tc.rosaText }]}>{t('pick.your_pick')}</Text>}
              </View>
              <View style={[styles.rgCount, { backgroundColor: mine ? tc.rosa : tc.bgCard3 }]}>
                <Text style={[styles.rgCountText, { color: mine ? tc.brandText : tc.text }]}>{g.count}</Text>
              </View>
            </View>
            <View style={styles.rgChips}>
              {owners.map(o => {
                const jc = jerseyColors[o.id_user]
                const isMe = meRow && o.id_user === meRow.id_user
                return (
                  <View key={o.id_user} style={[styles.rgChip, { backgroundColor: tc.bg, borderColor: jc ? legibleOn(jc, tc.bg, 1.6) : (isMe ? tc.rosa : tc.border), borderWidth: jc || isMe ? 1.5 : 1 }]}>
                    <PersonAvatarFace userId={o.id_user} settings={avatars[o.id_user]} size={22} />
                    <Text style={[styles.rgChipText, { color: isMe ? tc.rosaText : tc.text }]} numberOfLines={1}>{o.name}</Text>
                    {jc ? <MaterialCommunityIcons name="tshirt-crew" size={12} color={legibleOn(jc, tc.bg, 3)} /> : null}
                  </View>
                )
              })}
              {g.owners.length > 8 && !open && (
                <TouchableOpacity onPress={() => setExpanded(e => ({ ...e, [g.key]: true }))} style={styles.rgMore} accessibilityRole="button">
                  <Text style={[styles.rgChipText, { color: tc.textMuted }]}>+{g.owners.length - 8}</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )
      })}

      {flipList.length > 0 && (
        <View style={{ marginTop: 6 }}>
          {flipList.map(r => (
            <View key={r.name} style={styles.mxFlipRow}>
              <MaterialCommunityIcons name="swap-horizontal" size={12} color={colors.flip} />
              <Text style={styles.mxFlipText}>{r.name}</Text>
              <Text style={[styles.mxFlipSlot, { color: tc.textMuted }]}>{flips[r.id_user].rider_name_short}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  )
}

// A tappa conclusa: com'e' andata ai tuoi corridori (posizione e distacco), chi ha vinto e chi lo aveva, collegamento alla Classifica
function TourFinished({ rows, info, flips, meRow, idRace, idStage, leagueId, tc }) {
  const { t } = useTranslation()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { user } = useAuth()
  const router = useRouter()
  const [data, setData] = useState(null)
  const [mine, setMine] = useState(null)
  const avatars = useAvatars(rows.pivot.map(r => r.id_user))

  useEffect(() => {
    if (!idStage) return
    const ids = []
    Object.values(info.userRiders).forEach(m => Object.values(m).forEach(id => ids.push(id)))
    Object.values(flips).forEach(f => f.id_rider && ids.push(f.id_rider))
    let cancelled = false
    fetchStageResults(idStage, ids).then(r => { if (!cancelled) setData(r) }).catch(() => {})
    if (user?.id && leagueId) {
      supabase.rpc('fn_classifica_tappa', { p_id_league: leagueId }).select('posizione, id_user, gap_stage').eq('id_stage', idStage).eq('id_user', user.id).maybeSingle()
        .then(({ data: d }) => { if (!cancelled) setMine(d ?? null) }).catch(() => {})
    }
    return () => { cancelled = true }
  }, [idStage, info, flips, user?.id, leagueId])

  const model = useMemo(() => {
    if (!data) return null
    const ranks = Object.fromEntries(Object.entries(data.results).map(([id, r]) => [id, r.rank]))
    const eff = makeEffective(info, flips, ranks)
    const nameOf = (id) => { const rd = info.riders[id]; return rd ? (rd.labelNew || rd.label) : '' }
    const myPicks = meRow ? rows.slots.map(sl => { const id = eff(meRow, sl); return id ? { slot: sl, id, name: nameOf(id), ...(data.results[id] ?? {}) } : null }).filter(Boolean) : []
    const winner = data.winnerId ? {
      id: data.winnerId, name: nameOf(data.winnerId),
      owners: rows.pivot.filter(r => rows.slots.some(sl => eff(r, sl) === data.winnerId)),
    } : null
    return { myPicks, winner }
  }, [data, info, flips, rows, meRow])

  const goLeaderboard = () => router.push(`/leaderboard?id_race=${idRace}&id_stage=${idStage}`)
  const surname = (n) => String(n ?? '').trim().split(/\s+/)[0] || ''

  return (
    <View style={[styles.tfCard, { backgroundColor: tc.rosaSubtle, borderColor: tc.rosaBorder }]}>
      <TouchableOpacity onPress={goLeaderboard} activeOpacity={0.8} style={styles.tfHead} accessibilityRole="button" accessibilityLabel={t('pick.finished_cta')}>
        <MaterialCommunityIcons name="flag-checkered" size={20} color={tc.rosaText} />
        <Text style={{ flex: 1, fontSize: 13, color: tc.text }} numberOfLines={2}>
          {mine ? t('pick.stage_finished_banner', { pos: mine.posizione, gap: fmtGap(mine.gap_stage) || '—' }) : t('pick.finished_banner_nopos')}
        </Text>
        <Text style={{ fontSize: 12, fontFamily: fonts.condensedBold, color: tc.rosaText }}>{t('pick.finished_cta')}</Text>
        <MaterialCommunityIcons name="chevron-right" size={18} color={tc.rosaText} />
      </TouchableOpacity>
      {model && model.myPicks.length > 0 && (
        <View style={{ gap: 4, marginTop: 8 }}>
          <Text style={[styles.tfLabel, { color: tc.textMuted }]}>{t('pick.tour_your_result').toUpperCase()}</Text>
          {model.myPicks.map(k => (
            <View key={k.slot} style={styles.tfRow}>
              <Text style={[styles.tfName, { color: tc.text }]} numberOfLines={1}>{surname(k.name)}</Text>
              <Text style={[styles.tfRank, { color: tc.rosaText }]}>{k.dnf ? t('pick.result_dnf') : k.rank ? `${k.rank}°` : '—'}</Text>
              {!!fmtGap(k.gap) && <Text style={[styles.tfGap, { color: tc.textMuted }]}>{fmtGap(k.gap)}</Text>}
            </View>
          ))}
        </View>
      )}
      {model?.winner && (
        <View style={{ gap: 4, marginTop: 8 }}>
          <Text style={[styles.tfLabel, { color: tc.textMuted }]}>{t('pick.tour_stage_winner').toUpperCase()}</Text>
          <Text style={[styles.tfName, { color: tc.text }]} numberOfLines={1}>{model.winner.name || '—'}</Text>
          {model.winner.owners.length > 0 ? (
            <View style={styles.rgChips}>
              {model.winner.owners.slice(0, 10).map(o => (
                <View key={o.id_user} style={[styles.rgChip, { backgroundColor: tc.bg, borderColor: tc.border }]}>
                  <PersonAvatarFace userId={o.id_user} settings={avatars[o.id_user]} size={22} />
                  <Text style={[styles.rgChipText, { color: tc.text }]} numberOfLines={1}>{o.name}</Text>
                </View>
              ))}
            </View>
          ) : <Text style={[styles.tfGap, { color: tc.textMuted }]}>{t('pick.result_nobody')}</Text>}
        </View>
      )}
    </View>
  )
}

// ── Immagine da condividere (verticale, pensata per storie e post) ──────────
const SHARE_W = 360

function OdrShareCard({ tc, rows, info, stats, flips, nickname, shareHeader, shareMode }) {
  const { t, i18n } = useTranslation()
  const { keyOf, counts, top, uniqueCount, total } = stats
  const limit = shareMode === 'top10' ? 10 : shareMode === 'top25' ? 25 : 999
  const shown = rows.pivot.slice(0, limit)
  const hidden = total - shown.length
  const avatars = useAvatars(shown.map(r => r.id_user))
  const logo = raceLogo(shareHeader?.raceName)
  const year = shareHeader?.year ?? new Date().getFullYear()
  const dateLabel = shareHeader?.date ? new Date(shareHeader.date).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }) : String(year)
  const deep = darken(tc.rosa, 0.3)
  const accent = lighten(tc.rosa, 0.45)   // accento dell'immagine: il colore del brand, chiaro, leggibile sul fondo scuro

  return (
    <View style={{ width: SHARE_W, minHeight: (SHARE_W * 16) / 9, overflow: 'hidden', backgroundColor: '#0A0A10' }}>
      <LinearGradient colors={['#0A0A10', '#14080D', deep]} start={{ x: 0.1, y: 0 }} end={{ x: 0.9, y: 1 }} style={StyleSheet.absoluteFill} />
      <View style={{ position: 'absolute', top: -90, right: -90, width: 260, height: 260, borderRadius: 130, backgroundColor: tc.rosa + '26' }} />
      <View style={{ position: 'absolute', bottom: -130, left: -110, width: 320, height: 320, borderRadius: 160, backgroundColor: tc.rosa + '1A' }} />
      {logo && <Image source={logo} style={{ position: 'absolute', top: 30, right: -40, width: 220, height: 220, opacity: 0.12 }} resizeMode="contain" />}

      <View style={{ padding: 18 }}>
        {/* barra superiore */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Image source={LOGO} style={{ width: 28, height: 28 }} resizeMode="contain" />
            <Text style={{ color: '#fff', fontSize: 12, letterSpacing: 3, fontFamily: fonts.condensedBold }}>VIRTUA CYCLING</Text>
          </View>
          {!!shareHeader?.leagueName && <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 10, letterSpacing: 2, fontFamily: fonts.condensed }} numberOfLines={1}>{shareHeader.leagueName.toUpperCase()}</Text>}
        </View>

        {/* titolo */}
        <View style={{ marginTop: 26 }}>
          <Text style={{ color: accent, fontSize: 11, letterSpacing: 5, fontFamily: fonts.condensedBold }}>{t('pick.share_label')}</Text>
          <Text style={{ color: '#fff', fontSize: 36, lineHeight: 38, textTransform: 'uppercase', fontFamily: fonts.condensedBold, marginTop: 4 }} numberOfLines={3}>{shareHeader?.raceName}</Text>
          <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, letterSpacing: 1.5, marginTop: 6, fontFamily: fonts.condensed }}>{dateLabel.toUpperCase()} · {t('pick.scout_players', { count: total }).toUpperCase()}</Text>
        </View>

        {/* statistiche */}
        <View style={{ flexDirection: 'row', gap: 8, marginTop: 18 }}>
          <View style={shareStyles.stat}>
            <Text style={shareStyles.statLabel}>{t('pick.share_most_picked')}</Text>
            <Text style={shareStyles.statValue} numberOfLines={1}>{top?.name ?? '—'}</Text>
            <Text style={[shareStyles.statSub, { color: accent }]}>{top ? t('pick.sheet_chosen_by', { count: top.count, total }) : ''}</Text>
          </View>
          <View style={shareStyles.stat}>
            <Text style={shareStyles.statLabel}>{t('pick.share_unique_picks')}</Text>
            <Text style={shareStyles.statValue}>{uniqueCount}</Text>
            <Text style={[shareStyles.statSub, { color: accent }]}>{t('pick.share_unique_sub')}</Text>
          </View>
        </View>

        {/* matrice */}
        <View style={shareStyles.table}>
          <View style={{ flexDirection: 'row', paddingVertical: 6, paddingHorizontal: 6 }}>
            <View style={{ width: 82 }} />
            {rows.slots.map(sl => <Text key={sl} style={shareStyles.headCell}>{sl}</Text>)}
          </View>
          {shown.map((row, i) => {
            const isMe = row.name.toLowerCase() === nickname?.toLowerCase()
            return (
              <View key={row.name} style={[shareStyles.row, { backgroundColor: isMe ? tc.rosa + '55' : i % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.09)' }]}>
                <View style={{ width: 82, flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                  <PersonAvatarFace userId={row.id_user} settings={avatars[row.id_user]} size={22} />
                  <Text style={shareStyles.name} numberOfLines={1}>{row.name}</Text>
                </View>
                {rows.slots.map(sl => {
                  const parts = formatRiderNameParts(row.picks[sl])
                  return (
                    <Text key={sl} style={shareStyles.cell} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7}>
                      {parts.bottom || parts.top || '—'}
                    </Text>
                  )
                })}
              </View>
            )
          })}
          {hidden > 0 && <Text style={shareStyles.more}>{t('pick.share_hidden', { count: hidden })}</Text>}
        </View>

        {/* piede */}
        <View style={{ marginTop: 18, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={{ color: tc.rosaLight ?? tc.rosaText, fontSize: 14, letterSpacing: 1.5, fontFamily: fonts.condensedBold }}>#VIRTUACYCLING</Text>
          <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 9, letterSpacing: 2 }}>VIRTUACYCLING.COM</Text>
        </View>
      </View>
    </View>
  )
}


// Immagine da condividere per i giri: le scelte della tappa, per corridore
function TourShareCard({ tc, rows, info, stats, nickname, shareHeader, shareMode, view = 'rider', jerseyInfo = {} }) {
  const { t, i18n } = useTranslation()
  const { total, top } = stats
  const groups = useMemo(() => groupsOf(stats, info), [stats, info])
  const limit = shareMode === 'top10' ? 10 : shareMode === 'top25' ? 25 : 999
  const shown = groups.slice(0, limit)
  const hidden = groups.length - shown.length
  const playersShown = rows.pivot.slice(0, limit)
  const playersHidden = rows.pivot.length - playersShown.length
  const avatars = useAvatars(rows.pivot.map(r => r.id_user))
  const accent = lighten(tc.rosa, 0.45)
  const deep = darken(tc.rosa, 0.3)
  const dateLabel = shareHeader?.date ? new Date(shareHeader.date).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }) : ''
  const meId = rows.pivot.find(r => r.name.toLowerCase() === nickname?.toLowerCase())?.id_user
  const logo = raceLogo(shareHeader?.raceName)

  return (
    <View style={{ width: SHARE_W, minHeight: (SHARE_W * 16) / 9, overflow: 'hidden', backgroundColor: '#0A0A10' }}>
      <LinearGradient colors={['#0A0A10', '#14080D', deep]} start={{ x: 0.1, y: 0 }} end={{ x: 0.9, y: 1 }} style={StyleSheet.absoluteFill} />
      <View style={{ position: 'absolute', top: -90, right: -90, width: 260, height: 260, borderRadius: 130, backgroundColor: tc.rosa + '26' }} />
      <View style={{ position: 'absolute', bottom: -130, left: -110, width: 320, height: 320, borderRadius: 160, backgroundColor: tc.rosa + '1A' }} />
      {logo && <Image source={logo} style={{ position: 'absolute', top: 30, right: -40, width: 220, height: 220, opacity: 0.12 }} resizeMode="contain" />}
      <View style={{ padding: 18 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Image source={LOGO} style={{ width: 28, height: 28 }} resizeMode="contain" />
            <Text style={{ color: '#fff', fontSize: 12, letterSpacing: 3, fontFamily: fonts.condensedBold }}>VIRTUA CYCLING</Text>
          </View>
          {!!shareHeader?.leagueName && <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 10, letterSpacing: 2, fontFamily: fonts.condensed }} numberOfLines={1}>{shareHeader.leagueName.toUpperCase()}</Text>}
        </View>
        <View style={{ marginTop: 26 }}>
          <Text style={{ color: accent, fontSize: 11, letterSpacing: 5, fontFamily: fonts.condensedBold }}>{t('pick.share_label_stage')}</Text>
          <Text style={{ color: '#fff', fontSize: 36, lineHeight: 38, textTransform: 'uppercase', fontFamily: fonts.condensedBold, marginTop: 4 }} numberOfLines={3}>{shareHeader?.raceName}</Text>
          <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, letterSpacing: 1.5, marginTop: 6, fontFamily: fonts.condensed }}>
            {[`${t('common.stage')} ${shareHeader?.stage ?? ''}`.toUpperCase(), dateLabel.toUpperCase(), t('pick.scout_players', { count: total }).toUpperCase()].filter(Boolean).join(' · ')}
          </Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 8, marginTop: 18 }}>
          <View style={shareStyles.stat}>
            <Text style={shareStyles.statLabel}>{t('pick.share_most_picked')}</Text>
            <Text style={shareStyles.statValue} numberOfLines={1}>{top?.name ?? '—'}</Text>
            <Text style={[shareStyles.statSub, { color: accent }]}>{top ? t('pick.sheet_chosen_by', { count: top.count, total }) : ''}</Text>
          </View>
          <View style={shareStyles.stat}>
            <Text style={shareStyles.statLabel}>{t('pick.share_distinct_riders')}</Text>
            <Text style={shareStyles.statValue}>{groups.length}</Text>
            <Text style={[shareStyles.statSub, { color: accent }]}>{t('pick.share_unique_sub')}</Text>
          </View>
        </View>
        {view === 'player' ? (
          <View style={shareStyles.table}>
            {playersShown.map((row, i) => {
              const ji = jerseyInfo[row.id_user]
              const mine = row.id_user === meId
              const riderNames = rows.slots.map(sl => stats.nameOf[stats.keyOf(row.id_user, sl, row.picks[sl])]?.short).filter(Boolean).join(' · ')
              return (
                <View key={row.name} style={[tourShare.row, { backgroundColor: mine ? tc.rosa + '55' : i % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.09)', borderLeftWidth: ji ? 3 : 0, borderLeftColor: ji?.color ?? 'transparent', minHeight: ji ? 44 : 38 }]}>
                  {ji
                    ? <PersonAvatarBust userId={row.id_user} settings={avatars[row.id_user]} jersey={ji} width={30} height={32} />
                    : <PersonAvatarFace userId={row.id_user} settings={avatars[row.id_user]} size={24} />}
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={tourShare.rider} numberOfLines={1}>{row.name}</Text>
                    {ji && <Text style={[tourShare.owners, { color: legibleOn(ji.color, '#0B0B0B', 3) }]} numberOfLines={1}>{ji.label}</Text>}
                  </View>
                  <Text style={[tourShare.riderRight]} numberOfLines={2}>{riderNames || '—'}</Text>
                </View>
              )
            })}
            {playersHidden > 0 && <Text style={shareStyles.more}>{t('pick.share_hidden', { count: playersHidden })}</Text>}
          </View>
        ) : (
        <View style={shareStyles.table}>
          {shown.map((g, i) => {
            const mine = !!meId && g.owners.some(o => o.id_user === meId)
            return (
              <View key={g.key} style={[tourShare.row, { backgroundColor: mine ? tc.rosa + '55' : i % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.09)' }]}>
                <View style={tourShare.count}><Text style={tourShare.countText}>{g.count}</Text></View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={tourShare.rider} numberOfLines={1}>{g.short || g.name}</Text>
                  <Text style={tourShare.owners} numberOfLines={1}>{g.owners.map(o => o.name).join(', ')}</Text>
                </View>
                <View style={{ flexDirection: 'row', paddingLeft: 6 }}>
                  {g.owners.slice(0, 4).map((o, j) => (
                    <View key={o.id_user} style={{ marginLeft: j ? -8 : 0, zIndex: 4 - j }}>
                      <PersonAvatarFace userId={o.id_user} settings={avatars[o.id_user]} size={22} />
                    </View>
                  ))}
                </View>
              </View>
            )
          })}
          {hidden > 0 && <Text style={shareStyles.more}>{t('pick.share_hidden', { count: hidden })}</Text>}
        </View>
        )}
        <View style={{ marginTop: 18, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={{ color: tc.rosaLight ?? tc.rosaText, fontSize: 14, letterSpacing: 1.5, fontFamily: fonts.condensedBold }}>#VIRTUACYCLING</Text>
          <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 9, letterSpacing: 2 }}>VIRTUACYCLING.COM</Text>
        </View>
      </View>
    </View>
  )
}

const tourShare = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 8, paddingVertical: 6, minHeight: 38 },
  count: { minWidth: 24, height: 24, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.15)', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4 },
  countText: { color: '#fff', fontSize: 12, fontFamily: fonts.condensedBold },
  rider: { color: '#fff', fontSize: 12.5, textTransform: 'uppercase', fontFamily: fonts.condensedBold },
  owners: { color: 'rgba(255,255,255,0.6)', fontSize: 9.5, marginTop: 1 },
  riderRight: { maxWidth: 130, textAlign: 'right', color: 'rgba(255,255,255,0.92)', fontSize: 11, textTransform: 'uppercase', fontFamily: fonts.condensedBold },
})

const shareStyles = StyleSheet.create({
  stat: { flex: 1, backgroundColor: 'rgba(255,255,255,0.08)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.14)', borderRadius: 12, padding: 10 },
  statLabel: { color: 'rgba(255,255,255,0.6)', fontSize: 9, letterSpacing: 2, fontFamily: fonts.condensedBold },
  statValue: { color: '#fff', fontSize: 20, textTransform: 'uppercase', fontFamily: fonts.condensedBold, marginTop: 2 },
  statSub: { fontSize: 10, fontFamily: fonts.condensed, marginTop: 1 },
  table: { marginTop: 16, borderRadius: 14, overflow: 'hidden', backgroundColor: 'rgba(0,0,0,0.25)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)' },
  headCell: { flex: 1, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 9, fontFamily: fonts.condensedBold },
  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 6, minHeight: 34 },
  name: { flex: 1, color: '#fff', fontSize: 10.5, fontFamily: fonts.condensedBold },
  cell: { flex: 1, textAlign: 'center', color: 'rgba(255,255,255,0.92)', fontSize: 9.5, textTransform: 'uppercase', fontFamily: fonts.condensedBold, paddingHorizontal: 1 },
  more: { color: 'rgba(255,255,255,0.55)', fontSize: 9, letterSpacing: 2, textAlign: 'center', paddingVertical: 8 },
})

// ── Dettaglio della cinquina di un giocatore ────────────────────────────────
function PlayerPicksSheet({ row, rows, info, keyOf, counts, myKeys, isMe, total, flip, jersey, onClose }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const insets = useSafeAreaInsets()
  const avatars = useAvatars([row.id_user])

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.shOverlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
        <View style={[styles.shSheet, { backgroundColor: tc.bgCard, paddingBottom: insets.bottom + 12 }]}>
          <View style={[styles.shHeader, { borderBottomColor: tc.border }]}>
            {jersey
              ? <PersonAvatarBust userId={row.id_user} settings={avatars[row.id_user]} jersey={jersey} width={46} height={50} />
              : <PersonAvatarFace userId={row.id_user} settings={avatars[row.id_user]} size={40} />}
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Text style={[styles.shTitle, { color: isMe ? tc.rosaText : tc.text }]} numberOfLines={1}>{row.name}</Text>
              {row.isAutopick && <View style={styles.autoBadge}><Text style={styles.autoBadgeText}>A</Text></View>}
            </View>
            <Pressable onPress={onClose} hitSlop={6} style={[styles.shClose, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
              <MaterialCommunityIcons name="close" size={18} color={tc.text} />
            </Pressable>
          </View>
          <ScrollView contentContainerStyle={{ padding: 12, gap: 8 }}>
            {rows.slots.map(sl => {
              const short = row.picks[sl]
              if (!short) return null
              const key = keyOf(row.id_user, sl, short)
              const id = info.userRiders[row.id_user]?.[sl]
              const r = id ? info.riders[id] : null
              const full = r ? (r.labelNew || r.label) : short
              const shared = !isMe && myKeys.has(key)
              const unique = counts[key] === 1
              return (
                <View key={sl} style={[styles.shRow, { backgroundColor: tc.bgCard2, borderColor: shared ? tc.rosaBorder : tc.border }]}>
                  <View style={[styles.shSlot, { backgroundColor: tc.bgCard3 }]}>
                    <Text style={[styles.shSlotText, { color: tc.textMuted }]}>{sl}</Text>
                  </View>
                  {!info.national && r ? <TeamJersey kit={r.kit} size={44} /> : null}
                  {r?.id_national ? <NationalFlag code={r.id_national} /> : null}
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <RiderName name={info.national ? stripTeamBracket(full) : full} style={[styles.shRider, { color: tc.text }]} />
                    <View style={styles.shTags}>
                      <Text style={[styles.shCount, { color: tc.textMuted }]}>{t('pick.sheet_chosen_by', { count: counts[key] ?? 0, total })}</Text>
                      {shared && (
                        <View style={styles.shTag}>
                          <MaterialCommunityIcons name="account-check-outline" size={12} color={tc.rosaText} />
                          <Text style={[styles.shTagText, { color: tc.rosaText }]}>{t('pick.sheet_shared')}</Text>
                        </View>
                      )}
                      {unique && (
                        <View style={styles.shTag}>
                          <MaterialCommunityIcons name="star-four-points-outline" size={12} color={tc.rosaText} />
                          <Text style={[styles.shTagText, { color: tc.rosaText }]}>{t('pick.sheet_unique')}</Text>
                        </View>
                      )}
                    </View>
                  </View>
                </View>
              )
            })}
            {flip && (
              <View style={styles.mxFlipRow}>
                <MaterialCommunityIcons name="swap-horizontal" size={12} color={colors.flip} />
                <Text style={styles.mxFlipText}>{flip.rider_name_short}</Text>
                <Text style={[styles.mxFlipSlot, { color: tc.textMuted }]}>{t('pick.flip_slot_arrow', { slot: flip.id_slot })}</Text>
              </View>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  mxInfo: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', minHeight: 36, paddingHorizontal: 12 },
  mxInfoText: { flex: 1, fontSize: 12 },
  mxClear: { width: 36, height: 36, alignItems: 'center', justifyContent: 'center' },
  mxHead: { flexDirection: 'row', paddingHorizontal: 0, paddingBottom: 4, borderBottomWidth: 1, borderLeftWidth: 3, borderLeftColor: 'transparent' },
  mxHeadCell: { flex: 1, textAlign: 'center', fontFamily: fonts.condensedBold, fontSize: 11, letterSpacing: 0.5 },
  mxRow: { flexDirection: 'row', alignItems: 'stretch', borderLeftWidth: 3, minHeight: 48 },
  mxName: { justifyContent: 'center', paddingHorizontal: 6, paddingVertical: 4, gap: 3 },
  mxNameText: { fontFamily: fonts.condensedBold, fontSize: 13, lineHeight: 15 },
  mxCell: { flex: 1, borderLeftWidth: StyleSheet.hairlineWidth, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 2, paddingVertical: 4, minHeight: 48 },
  mxSurname: { fontFamily: fonts.condensedBold, fontSize: 12, lineHeight: 14, textAlign: 'center', textTransform: 'uppercase' },
  mxInitial: { fontSize: 11, lineHeight: 12, textAlign: 'center' },
  shOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  shSheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '80%' },
  shHeader: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderBottomWidth: 0.5 },
  shTitle: { fontFamily: fonts.condensedBold, fontSize: 20, flexShrink: 1 },
  shClose: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  shRow: { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 12, padding: 10, minHeight: 56 },
  shSlot: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center' },
  shSlotText: { fontFamily: fonts.condensedBold, fontSize: 13 },
  shRider: { fontSize: 15 },
  shTags: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 3 },
  shCount: { fontSize: 11 },
  shTag: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  shTagText: { fontSize: 11, fontFamily: fonts.condensedBold },
  tvToggle: { flexDirection: 'row', borderRadius: 10, padding: 3, marginHorizontal: 12, marginTop: 8, backgroundColor: tc.bgCard2 },
  tvToggleBtn: { flex: 1, minHeight: 36, alignItems: 'center', justifyContent: 'center', borderRadius: 8 },
  tvToggleText: { fontFamily: fonts.condensedBold, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 },
  rgCard: { marginHorizontal: 12, marginBottom: 6, borderWidth: 1, borderRadius: 12, padding: 10, gap: 8 },
  rgHead: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  rgName: { fontSize: 15 },
  rgMine: { fontSize: 11, fontFamily: fonts.condensedBold, letterSpacing: 0.5, textTransform: 'uppercase' },
  rgCount: { minWidth: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6 },
  rgCountText: { fontFamily: fonts.condensedBold, fontSize: 15 },
  rgChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  rgChip: { flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 16, paddingVertical: 3, paddingLeft: 3, paddingRight: 10, maxWidth: '100%' },
  rgChipText: { fontSize: 12, fontFamily: fonts.condensedBold, flexShrink: 1 },
  rgMore: { minHeight: 28, paddingHorizontal: 10, justifyContent: 'center' },
  tfCard: { marginHorizontal: 12, marginTop: 8, borderWidth: 1, borderRadius: 12, padding: 12 },
  tfHead: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 36 },
  tfLabel: { fontSize: 10, letterSpacing: 1.5, fontFamily: fonts.condensedBold },
  tfRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  tfName: { flex: 1, fontSize: 15, fontFamily: fonts.condensedBold },
  tfRank: { fontSize: 16, fontFamily: fonts.condensedBold },
  tfGap: { fontSize: 12 },
  sgPlayer: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 8, paddingLeft: 10, paddingVertical: 6 },
  sgJersey: { fontSize: 11, fontFamily: fonts.condensedBold, letterSpacing: 0.3 },
  sgPlayerName: { flexShrink: 1, fontFamily: fonts.condensedBold, fontSize: 14 },
  sgRider: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 8, paddingHorizontal: 10, paddingVertical: 6, width: '60%', alignSelf: 'stretch' },
  sgRiderName: { fontSize: 14 },
  sgCountPill: { borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3 },
  sgCountText: { fontSize: 11, fontFamily: fonts.condensedBold },
  sgCount: { fontSize: 11, marginTop: 1 },
  mxUnique: { position: 'absolute', top: 4, right: 4, width: 5, height: 5, borderRadius: 3 },
  mxFlipIcon: { position: 'absolute', bottom: 3, right: 3 },
  mxFlipRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 3, backgroundColor: colors.flipBg },
  mxFlipText: { fontSize: 11, color: colors.flip, fontFamily: fonts.condensedBold, textTransform: 'uppercase' },
  mxFlipSlot: { fontSize: 11 },
  tourCard: { borderRadius: 12, borderWidth: 1, borderLeftWidth: 3, borderColor: tc.border, borderLeftColor: tc.border, backgroundColor: tc.bgCard, overflow: 'hidden', marginBottom: 8 },
  tourCardMe: { backgroundColor: tc.rosaSubtle, borderColor: tc.rosaBorder, borderLeftColor: tc.rosa },
  tourHeader: { flexDirection: 'row', alignItems: 'center', padding: 9, paddingHorizontal: 12 },
  tourName: { fontFamily: fonts.condensedBold, fontSize: 12, color: tc.text, textTransform: 'uppercase', letterSpacing: 0.5 },
  tourNameMe: { color: tc.rosaText },
  youBadge: { fontSize: 10, color: tc.rosaText, fontFamily: fonts.condensed },
  autoBadge: { backgroundColor: '#6366f1', borderRadius: 4, paddingHorizontal: 4, paddingVertical: 1 },
  autoBadgeText: { fontFamily: fonts.condensedBold, color: '#fff', fontSize: 9 },
  riderSurname: { fontFamily: fonts.condensedBold, fontSize: 11, color: tc.text, textTransform: 'uppercase' },
  riderFirst: { fontSize: 9, color: tc.textMuted },
  flipChip: { flexDirection: 'row', alignItems: 'center', gap: 6, margin: 6, marginHorizontal: 12, backgroundColor: colors.flipBg, borderWidth: 1, borderColor: colors.flipBorder, borderRadius: 6, padding: 5, paddingHorizontal: 8 },
  flipChipText: { fontSize: 10, color: colors.flip, fontFamily: fonts.condensedBold, textTransform: 'uppercase', letterSpacing: 0.3 },
  flipChipSlot: { fontSize: 9, color: tc.textMuted },
})